
import React from 'react';
import { cn } from '@/lib/utils';
import { Class } from '@/types/classTypes';
import { format } from 'date-fns';

interface CalendarEventProps {
  classData: Class;
  onClick: (e: React.MouseEvent) => void;
  enrolledCount?: number;
  isFullscreen?: boolean;
}

export const CalendarEvent: React.FC<CalendarEventProps> = ({ classData, onClick, enrolledCount, isFullscreen = false }) => {
  // Get color based on class name with Sawyer-level professional styling
  const getEventColor = (className: string): string => {
    const colors = {
      'Ballet': 'bg-gradient-to-br from-pink-100 to-pink-200 text-pink-900 border-l-pink-500 hover:from-pink-200 hover:to-pink-300',
      'Hip Hop': 'bg-gradient-to-br from-orange-100 to-orange-200 text-orange-900 border-l-orange-500 hover:from-orange-200 hover:to-orange-300',
      'Painting': 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-900 border-l-emerald-500 hover:from-emerald-200 hover:to-emerald-300',
      'Music': 'bg-gradient-to-br from-purple-100 to-purple-200 text-purple-900 border-l-purple-500 hover:from-purple-200 hover:to-purple-300',
      'Dance': 'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-900 border-l-blue-500 hover:from-blue-200 hover:to-blue-300',
      'Gymnastics': 'bg-gradient-to-br from-teal-100 to-teal-200 text-teal-900 border-l-teal-500 hover:from-teal-200 hover:to-teal-300',
      'Martial Arts': 'bg-gradient-to-br from-red-100 to-red-200 text-red-900 border-l-red-500 hover:from-red-200 hover:to-red-300',
      'Swimming': 'bg-gradient-to-br from-cyan-100 to-cyan-200 text-cyan-900 border-l-cyan-500 hover:from-cyan-200 hover:to-cyan-300',
      'Jazzercise': 'bg-gradient-to-br from-violet-100 to-violet-200 text-violet-900 border-l-violet-500 hover:from-violet-200 hover:to-violet-300',
      'default': 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-900 border-l-slate-500 hover:from-slate-200 hover:to-slate-300'
    };

    const matchingKey = Object.keys(colors).find(key => 
      className.toLowerCase().includes(key.toLowerCase())
    );
    return colors[matchingKey as keyof typeof colors] || colors.default;
  };

  const colorClass = getEventColor(classData.name);
  const maxStudents = classData.max_students || 0;
  const actualEnrolledCount = enrolledCount ?? 0;
  
  // Get the start time from the first schedule or event
  const startTime = classData.is_event 
    ? classData.event_start_time 
    : (classData.schedules?.[0]?.start_time || '');

  if (isFullscreen) {
    // Compact version for fullscreen
    return (
      <div
        className={cn(
          "px-3 py-2 rounded-lg border-l-4 text-xs cursor-pointer transition-all duration-200",
          colorClass,
          "hover:shadow-md group"
        )}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-bold truncate flex items-center gap-2">
              {startTime && (
                <span className="font-medium opacity-90">
                  {format(new Date(`2000-01-01T${startTime}`), 'h:mm')}
                </span>
              )}
              <span className="truncate">{classData.name}</span>
            </div>
          </div>
          
          {maxStudents > 0 && (
            <div className="text-[10px] font-bold ml-2 shrink-0">
              {actualEnrolledCount}/{maxStudents}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular version
  return (
    <div
      className={cn(
        "px-4 py-3 rounded-xl border-l-4 text-sm cursor-pointer transition-all duration-200 shadow-md",
        colorClass,
        "hover:shadow-lg hover:scale-[1.03] group relative overflow-hidden"
      )}
      onClick={onClick}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent pointer-events-none" />
      
      <div className="relative z-10">
        {/* Time and Title Row */}
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            {startTime && (
              <div className="text-xs font-bold opacity-90 mb-1">
                {format(new Date(`2000-01-01T${startTime}`), 'h:mm a')}
              </div>
            )}
            <div className="font-bold truncate text-sm leading-tight">
              {classData.name}
            </div>
          </div>
          
          {maxStudents > 0 && (
            <div className="text-xs font-black ml-3 shrink-0 px-2 py-1 bg-white/90 rounded-full shadow-sm border">
              {actualEnrolledCount}/{maxStudents}
            </div>
          )}
        </div>
        
        {/* Capacity indicator bar */}
        {maxStudents > 0 && (
          <div className="w-full bg-white/40 rounded-full h-1 mt-2">
            <div 
              className="bg-white/80 h-1 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((actualEnrolledCount / maxStudents) * 100, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
